import { SwiperOptions } from 'swiper/types';
import { Attachment } from '.';

export enum DynamicSectionTypes {
  DYNAMIC_PRODUCTS = 'dynamic-products',
  RECOMMENDATIONS = 'recommendations',
  CATEGORIES_PRODUCTS = 'categories-products',
  HANDPIKED_PRODUCTS = 'handpiked-products',
  IMAGE_SLIDER = 'image-slider',
  IMAGE_GRID = 'image-grid',
  PRODUCT_CATEGORIES = 'product-categories',
  DELIVERY_BANNER = 'delivery-banner',
  PARTNER_STORES = 'partner-stores',
  LABORATORY_PROGRAMS = 'laboratory-programs',
  DYNAMIC_CATEGORIES_LINKS = 'dynamic-categories-links',
}

/** Recommendation type selectable by admin for the recommendations section (FBT and CAB are not selectable; they are implemented in the API only). */
export type RecommendationSectionType = 'trending' | 'last-seen';

/** Params for recommendations section (admin payload) */
export type RecommendationsSectionParams = {
  limit?: number;
  /** For trending: context scope. Omit or "global" = global trending */
  context?: 'global' | 'type' | 'category';
  /** For trending: type_id or category_id when context is "type" or "category" */
  id?: number;
};

/**
 * Admin payload shape for the recommendations dynamic section.
 * Send this structure from the admin app to render a recommendations block.
 *
 * @example
 * // Global trending products (grid)
 * {
 *   "enabled": true,
 *   "order": 2,
 *   "type": "recommendations",
 *   "component": "products-grid",
 *   "title": "Em alta",
 *   "recommendation_type": "trending",
 *   "recommendation_params": { "limit": 12 }
 * }
 *
 * @example
 * // Trending by category (slider)
 * {
 *   "enabled": true,
 *   "order": 3,
 *   "type": "recommendations",
 *   "component": "products-slider",
 *   "title": "Trending em Vitaminas",
 *   "recommendation_type": "trending",
 *   "recommendation_params": { "limit": 10, "context": "category", "id": 157 }
 * }
 *
 * @example
 * // Last seen products
 * {
 *   "enabled": true,
 *   "order": 4,
 *   "type": "recommendations",
 *   "component": "products-grid",
 *   "title": "Vistos recentemente",
 *   "recommendation_type": "last-seen",
 *   "recommendation_params": { "limit": 8 }
 * }
 */

export enum DynamicSectionComponents {
  PRODUCTS_GRID = 'products-grid',
  CATEGORIES_PRODUCTS = 'categories-products',
  PRODUCTS_SLIDER = 'products-slider',
  IMAGE_SLIDER = 'image-slider',
  IMAGE_SLIDER_BREAKPOINT = 'image-slider-breakpoint',
  IMAGE_GRID = 'image-grid',
  CATEGORIES_SLIDER = 'categories-slider',
  CATEGORIES_VERTICAL_MENU = 'categories-vertical-menu',
  DELIVERY_BANNER = 'delivery-banner',
  PARTNER_STORES = 'partner-stores',
  LABORATORY_PROGRAMS = 'laboratory-programs',
  DYNAMIC_CATEGORIES_LINKS = 'dynamic-categories-links',
}

export type TSectionStyles = {
  title?: {
    color?: string;
    textAlign?: 'left' | 'center' | 'right';
    fontSize?: string;
    fontWeight?: string | number;
    lineHeight?: string;
    letterSpacing?: string;
    margin?: string;
  };
};

export const DEFAULT_SECTION_STYLE: TSectionStyles = {
  title: {
    color: '#197fdb',
    textAlign: 'center',
    // fontSize: '1.5rem',
  },
};

export interface ISectionAttachment extends Attachment {
  title?: string;
  url?: string;
  width?: number;
  height?: number;
}

export type TDynamicSection = {
  /** Stable id from admin (used for analytics attribution). */
  id?: string;
  enabled?: boolean;
  order?: number;
  title?: string;
  styles?: TSectionStyles;
  type?: DynamicSectionTypes;
  component?: DynamicSectionComponents;
  products?: string[];
  filters?: {
    raw: string;
  };
  ordering?: {
    raw?: string;
  };
  items_per_page?: number;
  /** For type === RECOMMENDATIONS: which recommendation API to use */
  recommendation_type?: RecommendationSectionType;
  /** For type === RECOMMENDATIONS: API params (limit, context, id) */
  recommendation_params?: RecommendationsSectionParams;
  // image slider
  params?: SwiperOptions & {
    maxWidth?: number;
  } & {
    grid: Array<{
      breakpoint: string;
      selectedMedia: Array<number | string> | null | undefined;
      cols: number;
      rowHeight: number;
      layout: any[];
    }>;
  };
  files?: ISectionAttachment[];
};
